/**
 * The object containing Card data.
 * @typedef {Object} mapCCESItemToCardObject_Return_Asset
 * @property {'icon'} type - Asset type is icon.
 * @property {string} url - Icon url.
 */

/**
 * The object containing Card data.
 * @typedef {Object} mapCCESItemToCardObject_Return
 * @property {string} id - Application id.
 * @property {string} name - Application name.
 * @property {string} summary - Application summary (UXP's character limit is 30, meanwhile ZXP's one is 170).
 * @property {string} price - Application price. It'a **string** because it was clamped to two decimals.
 * @property {boolean} hasTrial - If this application has a trial or not.
 * @property {'CC'} type - Application type is CC.
 * @property {Object} publisher - Application's publisher.
 * @property {string} publisher.id - Application's publisher id.
 * @property {string} publisher.name - Application's publisher name.
 * @property {number} rating - The overall rating of the application.
 * @property {mapCCESItemToCardObject_Return_Asset[]} assets - Array containing the smallest icon.
 * @property {string[]} supportedProducts - Array containing the unique supported products SAP codes.
 */

/**
 * This function takes the CCES item and returns an object with only the properties that are needed for the Card component.
 * @param {Object} data The CCES item data
 * @returns {mapCCESItemToCardObject_Return} The object containing Card data.
 */
export function mapCCESItemToCardObject(data) {
  return {
    id: data.id,
    name: data.localizedMetadata.name,
    summary: data.localizedMetadata.summary,
    price: data.pricingInfo.price.toFixed(2),
    subscription: data.pricingInfo.subscription,
    hasTrial: data.trialInfo.linkedAppId && data.trialInfo.linkedAppId !== '0' ? true : false,
    type: 'CC',
    publisher: {
      id: data.owner.id,
      name: data.owner.name,
    },
    rating: data.rating.overallRating,
    assets: [data.icons.sort((a, b) => a.width - b.width).map((icon) => ({ type: 'icon', url: icon.href }))[0]],
    supportedProducts: Array.from(new Set(data.version.hostAppMapping.map((element) => element.hostAppCode))),
    requiresPayment: data.version.dependency.service.requiresPayment,
  };
}

/**
 * The object containing Assets data.
 * @typedef {Object} mapCCESItemToDetailsObject_Return_Asset
 * @property {string} type - Asset type can be icon, preview and document.
 * @property {string} url -  Url of the icon, preview and document.
 * @property {string} name - Name of the document, exists only for the documents.
 */

/**
 * The object containing Details page data.
 * @typedef {Object} mapCCESItemToDetailsObject_Return
 * @property {string} id - Application id.
 * @property {string} name - Application name.
 * @property {string} summary - Application summary (UXP's character limit is 30, meanwhile ZXP's one is 170).
 * @property {string} price - Application price. It'a **string** because it was clamped to two decimals.
 * @property {boolean} hasTrial - If this application has a trial or not.
 * @property {string} type - Application type (UXP or ZXP).
 * @property {Object} publisher - Application's publisher.
 * @property {string} publisher.id - Application's publisher id.
 * @property {string} publisher.name - Application's publisher name.
 * @property {import("../../../../apps/__container/src/pages/components/TraderInformation/types").PublisherContactDetails} publisher.contactDetails - If present, application's publisher contact details.
 * @property {number} rating - The overall rating of the application.
 * @property {mapCCESItemToDetailsObject_Return_Asset[]} assets - Array containing the icons, preview images and documents.
 * @property {string[]} supportedProducts - Array containing the unique supported products SAP codes.
 * @property {string} releaseNotes - Release notes for the Application.
 * @property {string[]} languages - Array containing the unique language codes.
 * @property {string} whereToFind - Where to find Plungin information.
 * @property {string[]} platforms - array containing supported OS.
 * @property {boolean} requiresInstallation - If Plugin requires installation of another application on same device(Exists only for UXP).
 * @property {boolean} requiresSignIn - If Plugin requires login to 3rd party service.
 * @property {string} privacyPolicy - Url to the Read about privacy policy of the plugin.
 * @property {string} licenseAgreement - Url to the Read about license information about the plugin.
 */

/**
 * This function takes the CCES item and returns an object with the properties that are needed for the Details page.
 * @param {Object} data The CCES item data
 * @returns {mapCCESItemToDetailsObject_Return} The object containing Details data.
 */
export function mapCCESItemToDetailsObject(data) {
  return {
    id: data.id,
    name: data.localizedMetadata.name,
    summary: data?.localizedMetadata?.summary,
    price: data?.pricingInfo?.price?.toFixed(2),
    hasTrial: data?.trialInfo?.linkedAppId ? true : false,
    appType: data?.type,
    publisher: {
      id: data?.owner?.id,
      name: data?.owner?.name,
      website: data?.owner?.website,
      contactDetails: data?.owner?.contactDetails,
    },
    publishedDate: data?.firstPublishedAt,
    lastUpdatedDate: data?.version?.publishedAt,
    packageSize: data?.version?.packageSize,
    rating: data?.rating?.overallRating,
    histogram: data?.rating?.histogram,
    assets: getAssets(data?.icons, data?.version?.media, data?.version?.localizedMetadata?.documents),
    supportedProducts: data?.version?.hostAppMapping || [],
    description: data?.version?.localizedMetadata?.description,
    releaseNote: data?.version?.localizedMetadata?.releaseNotes,
    languages: data?.version?.languageCodes,
    whereToFind: data?.version?.localizedMetadata?.whereToFind,
    platforms: data?.version?.dependency?.app?.platforms,
    requiresInstallation: data?.version?.dependency?.app?.requireInstallation,
    requiresSignIn:
      data?.version?.dependency?.service?.servicesSignIn || data?.version?.dependency?.service?.serviceSignIn,
    requiresPayment: data?.version?.dependency?.service?.requiresPayment,
    privacyPolicy: data?.version?.localizedMetadata?.privacyPolicy,
    licenseAgreement: data?.version?.localizedMetadata?.eula ?? data?.version?.localizedMetadata?.licenseAgreement,
    version: data?.version?.versionString,
    licensing: data?.version?.licensing,
    cctInstallInstructions: data?.version?.localizedMetadata?.cctInstallInstructions,
    support: data?.version?.localizedMetadata?.helpUrl
      ? {
          url: data?.version?.localizedMetadata?.helpUrl,
        }
      : null,
    customTags: data?.localizedMetadata?.keywords || [],
    pricingInfo: data?.pricingInfo,
    trialInfo: formatTrialInfo(data?.trialInfo),
    appVersionId: data?.version?.id,
    mediaList: formatMedia(data?.version?.media),
    categoryIds: data?.categoryIds,
  };
}

function getAssets(icons, media, documents) {
  const iconList = icons?.length
    ? [icons.sort((a, b) => a.width - b.width).map((icon) => ({ type: 'icon', url: icon?.href }))[0]]
    : [];
  const screenshotList = formatMedia(media);
  const documentList = documents?.length
    ? documents.map((document) => ({ type: 'document', url: document?.link, name: document?.name }))
    : [];
  const assets = [...iconList, ...screenshotList, ...documentList];
  return assets;
}

function formatMedia(media) {
  const formattedMedia = media?.length
    ? media.map((mediaItem) => {
        if (mediaItem.type === 'SCREENSHOT') {
          return { type: 'preview', url: mediaItem?.href, caption: mediaItem?.caption };
        } else if (mediaItem.type === 'VIDEO') {
          return { type: 'video', url: mediaItem?.href, caption: mediaItem?.caption };
        } else if (mediaItem.type === 'DOCUMENT') {
          return { type: 'document', url: mediaItem?.href, name: mediaItem?.href };
        }
      })
    : [];
  return formattedMedia;
}

/*linkedAppId data returns as "0" instead of null or undefined
 Till this data issue is resolved we need below mentioned code*/
function formatTrialInfo(trialData) {
  if (trialData?.linkedAppId === '0') {
    trialData.linkedAppId = null;
  }
  return trialData;
}

//TODO: Remove this function once the ec preview API response returns data in required format
export function mapECItemToDetailsObject(data) {
  return {
    id: data.id,
    name: data.localizedMetadata.name,
    summary: data?.localizedMetadata?.summary,
    price: data?.pricingInfo?.price?.toFixed(2),
    appType: data?.type,
    publisher: {
      id: data?.owner?.id,
      name: data?.owner?.name,
      website: data?.owner?.website,
    },
    publishedDate: data?.publishedDate,
    lastUpdatedDate: data?.version?.publishedAt,
    assets: getAssets(data?.icons, data?.version?.media, data?.version?.localizedMetadata?.documents),
    hostApps: data?.version?.hostAppMapping?.map((hostApp) => hostApp?.hostAppCode) || [],
    description: data?.version?.localizedMetadata?.description,
    releaseNote: data?.version?.localizedMetadata?.releaseNotes,
    languages: data?.version?.languageCodes,
    requiresInstallation: data?.version?.dependency?.app?.requireInstallation,
    requiresSignIn: data?.version?.dependency?.service?.serviceSignIn,
    requiresPayment: data?.version?.dependency?.service?.requiresPayment,
    privacyPolicy: data?.version?.localizedMetadata?.privacyPolicy,
    licenseAgreement: data?.version?.localizedMetadata?.eula,
    version: data?.version?.versionString,
    optionalProducts: data?.version?.optionalProducts,
    support: {
      url: data?.version?.localizedMetadata?.helpUrl,
      email: data?.version?.localizedMetadata?.supportEmail,
    },
    customTags: data?.localizedMetadata?.keywords || [],
    pricingInfo: data?.pricingInfo,
    versionId: data?.version?.id,
    mediaList: formatMedia(data?.version?.media),
    isDownloadable: data?.version?.isDownloadable,
    licensing: data?.version?.licensing,
  };
}
